import type { User } from '../../types'
import type {
    IChat,
    IChatFile,
    ICreateChat,
    ICreateGroupChat,
    ISendMessage,
    IMessage,
    IUpdateChat,
} from '../../types/chats'
import { baseApi } from '../baseApi'
import type { ResponseType } from '../types'

const authBase = 'api/chats'

export const chatsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getAllChats: builder.query<ResponseType<IChat[]>, string>({
            query: (query) => `${authBase}?${query}`,
            providesTags: ['chats'],
            keepUnusedDataFor: 0,
        }),
        getChatById: builder.query<
            ResponseType<IChat>,
            { id: string; query?: string }
        >({
            query: ({ id, query }) =>
                query ? `${authBase}/${id}?${query}` : `${authBase}/${id}`,
        }),

        getChatByOrder: builder.query<
            ResponseType<number>,
            { orderId: string }
        >({
            query: ({ orderId }) => `${authBase}/orders/${orderId}`,
        }),
        getSearchMessages: builder.query<
            ResponseType<IMessage[]>,
            { chatId: string; query: string }
        >({
            query: ({ chatId, query }) =>
                `${authBase}/messages/${chatId}?${query}`,
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                // Ключ для кеша учитывает chatId и query
                return `${endpointName}-${queryArgs.chatId}`
            },
            // Always merge incoming data to the cache entry
            merge: (currentCache, newItems) => {
                const isPagination = newItems.pagination?.page !== 1
                currentCache.data = isPagination
                    ? [...currentCache.data, ...newItems.data] // Добавляем данные при пагинации
                    : newItems.data // Заменяем данные при новом поиске
            },
            forceRefetch({ currentArg, previousArg }) {
                // Перезапрашиваем данные, если изменились параметры
                console.log(currentArg, previousArg)
                return currentArg?.query !== previousArg?.query
            },
        }),
        getChatFiles: builder.query<
            ResponseType<IChatFile[]>,
            { id: string; query?: string }
        >({
            query: ({ id, query }) =>
                query
                    ? `${authBase}/files/${id}?${query}`
                    : `${authBase}/files/${id}`,
        }),
        createChatByOrder: builder.mutation<ResponseType<IChat>, ICreateChat>({
            query: ({ id, data }) => ({
                url: `${authBase}/orders/${id}`,
                method: 'POST',
                body: data,
            }),
        }),
        createChatByUser: builder.mutation<ResponseType<IChat>, ICreateChat>({
            query: ({ id, data }) => ({
                url: `${authBase}/users/${id}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['chats'],
        }),
        createGroupChat: builder.mutation<
            ResponseType<IChat>,
            ICreateGroupChat
        >({
            query: (data) => ({
                url: `${authBase}/group`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['chats'],
        }),
        sendMessage: builder.mutation<ResponseType<IMessage>, ISendMessage>({
            query: ({ id, data }) => ({
                url: `${authBase}/messages/${id}`,
                method: 'POST',
                body: data,
            }),
        }),
        updateMessage: builder.mutation<ResponseType<IMessage>, ISendMessage>({
            query: ({ id, data }) => ({
                url: `${authBase}/messages/${id}`,
                method: 'PUT',
                body: data,
            }),
        }),
        blockUser: builder.mutation<ResponseType<boolean>, string>({
            query: (id) => ({
                url: `${authBase}/messages/${id}`,
                method: 'PUT',
            }),
        }),
        updateChat: builder.mutation<
            ResponseType<{
                id: number
                isGroup: boolean
                name: string
                users: User[]
            }>,
            { id: number; data: IUpdateChat }
        >({
            query: ({ id, data }) => ({
                url: `${authBase}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['chats'],
        }),
        marcUsRead: builder.mutation<
            ResponseType<IChat[]>,
            { messageIds: number[] }
        >({
            query: (data) => ({
                url: `${authBase}/messages/mark-as-read`,
                method: 'PATCH',
                body: data,
            }),
        }),
        removeMessage: builder.mutation<ResponseType<{ id: number }>, number>({
            query: (id) => ({
                url: `${authBase}/messages/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (
                response: ResponseType<{}>,
                meta,
                id: number,
            ) => ({
                success: response.success,
                data: { id },
            }),
        }),
        removeChat: builder.mutation<ResponseType<{ id: number }>, number>({
            query: (id) => ({
                url: `${authBase}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['chats'],
            transformResponse: (
                response: ResponseType<{}>,
                meta,
                id: number,
            ) => ({
                success: response.success,
                data: { id },
            }),
        }),
        leaveChat: builder.mutation<ResponseType<{ id: number }>, number>({
            query: (id) => ({
                url: `${authBase}/${id}`,
                method: 'PATCH',
            }),
            transformResponse: (
                response: ResponseType<{}>,
                meta,
                id: number,
            ) => ({
                success: response.success,
                data: { id },
            }),
        }),
    }),
})

export const {
    useGetAllChatsQuery,
    useLazyGetChatByIdQuery,
    useGetChatByIdQuery,
    useGetChatByOrderQuery,
    useGetChatFilesQuery,
    useCreateChatByOrderMutation,
    useCreateChatByUserMutation,
    useCreateGroupChatMutation,
    useSendMessageMutation,
    useUpdateMessageMutation,
    useBlockUserMutation,
    useUpdateChatMutation,
    useMarcUsReadMutation,
    useRemoveMessageMutation,
    useRemoveChatMutation,
    useLeaveChatMutation,
    useGetSearchMessagesQuery,
} = chatsApi
